import { Grid, GridItem, Box, Text, Flex } from '@chakra-ui/react';
import styles from './SpNftMarketPlaceList.module.scss';
import React, { FC, useState } from 'react';
import { ListLoader } from '@app/screens/pools/components/ListLoader';
import { useGetNftMarketplaceListings } from '@app/hooks/nft/useGetNftMarketplaceListings';
import { SpNFTFilter } from '@app/screens/nft-marketplace/SpNFTFilter';
import { ListPagination } from '@app/screens/pools/components/ListPagination';
import { useCheckUserMarketplaceEligibility } from '@app/hooks/nft/useCheckUserMarketplaceEligibility';
import { StaggeredList } from '@app/components/StaggeredList';
import { AnimatePresence } from 'framer-motion';
import { PairToken } from '@app/types/pool';
import { SprNftMarketplaceRowItem } from '@app/screens/nft-marketplace/SpNftMarketPlaceList/SprNftMarketplaceCell';
import { useNftMarketplaceFiltersState } from '@app/state/nftMarketplaceFiltersStore';
import { useMedia } from 'react-use';

const ITEMS_PER_PAGE = 12;

interface Props {
  isMyListingsOnly?: boolean;
}

const SpNftMarketPlaceList: FC<Props> = ({ isMyListingsOnly }) => {
  // const [activePage, setActivePage] = useState(0);
  const isMobile = useMedia('(max-width: 1024px)', true);
  const {
    activePage,
    actions: { setActivePage }
  } = useNftMarketplaceFiltersState();
  const { data, isLoading } = useGetNftMarketplaceListings(
    isMyListingsOnly,
    ITEMS_PER_PAGE,
    activePage * ITEMS_PER_PAGE
  );

  useCheckUserMarketplaceEligibility();

  const renderRows = () => {
    if (isLoading) {
      return <ListLoader />;
    }

    if (!data || data.length === 0) {
      return (
        <Flex minHeight="300px" width="100%" align="center" justify="center">
          <Text color="neutral.400" fontSize="32px">
            No data found
          </Text>
        </Flex>
      );
    }

    const startIndex = activePage * ITEMS_PER_PAGE; // calculate the starting index
    const endIndex = startIndex + ITEMS_PER_PAGE; // calculate the ending index

    // const paginatedData = data?.slice(startIndex, endIndex);

    return (
      <AnimatePresence>
        <StaggeredList key={data[0]?.id}>
          {data.map(item => (
            <SprNftMarketplaceRowItem
              key={item.id}
              isMobile={isMobile}
              poolAddress={item.nftPool.id}
              tokenId={item.tokenId}
              tokenDetails={item.nftPool.lpToken as PairToken}
              data={item}
            />
          ))}
        </StaggeredList>
      </AnimatePresence>
    );
  };

  const renderTableHeader = () => {
    if (!isMobile) {
      return (
        <Grid className={styles.tableHeader}>
          <GridItem display="flex" justifyContent="center">
            <Text>Pool</Text>
          </GridItem>
          <GridItem display="flex" justifyContent="flex-end">
            <Text>Vault</Text>
          </GridItem>
          <GridItem>
            <Text>APR</Text>
          </GridItem>
          <GridItem>
            <Text>Properties</Text>
          </GridItem>
          <GridItem>
            <Text>Locked Until</Text>
          </GridItem>
          <GridItem>
            <Text>Auction Type</Text>
          </GridItem>
          <GridItem>
            <Text>Deposits</Text>
          </GridItem>
          <GridItem>
            <Text color="primaryWhite">Sell Price</Text>
          </GridItem>
          <GridItem>
            <Text>Discount</Text>
          </GridItem>
          <GridItem></GridItem>
        </Grid>
      );
    }

    return null;
  };

  const renderFilters = () => {
    if (!isMobile) {
      return <SpNFTFilter />;
    }

    return null;
  };

  const renderMobileFilters = () => {
    if (isMobile) {
      return <SpNFTFilter isMobile={isMobile} />;
    }

    return null;
  };

  return (
    <>
      {renderFilters()}
      <Box className={styles.tableContainer}>
        {renderMobileFilters()}
        {renderTableHeader()}
        {renderRows()}
      </Box>
      {
        <ListPagination
          activePage={activePage}
          // totalPages={Math.ceil(data?.length / ITEMS_PER_PAGE)}
          onPrev={() => setActivePage(activePage - 1)}
          onNext={() => setActivePage(activePage + 1)}
          hasNext={!!data?.length && data.length >= ITEMS_PER_PAGE}
        />
      }
    </>
  );
};

export default SpNftMarketPlaceList;
